import ButtonPrimary, {
  EButtonSize,
} from "@rentiohq/shared-frontend/dist/components/components/ButtonPrimary";
import FormSectionSubtitle from "@rentiohq/shared-frontend/dist/components/components/FormSectionSubtitle";
import FullScreenActivityIndicator from "@rentiohq/shared-frontend/dist/components/components/FullScreenActivityIndicator";
import { FullScreenErrorState } from "@rentiohq/shared-frontend/dist/components/components/FullScreenState";
import ListItemSelect from "@rentiohq/shared-frontend/dist/components/components/ListItemSelect";
import ListItemSeparator from "@rentiohq/shared-frontend/dist/components/components/ListItemSeparator";
import PaymentRequestFetch from "@rentiohq/shared-frontend/dist/components/components/PaymentRequestFetch";
import PropertyFetchListItem from "@rentiohq/shared-frontend/dist/components/components/PropertyFetchListItem";
import Spacer, {
  ESpacerWeight,
} from "@rentiohq/shared-frontend/dist/components/components/Spacer";
import * as paymentApi from "@rentiohq/shared-frontend/dist/redux/payment/payment.api";
import { showAlert } from "@rentiohq/shared-frontend/dist/utils/alert/alert.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  EUIPaymentMethod,
  getAvailableOncePaymentMethods,
  getAvailableRecurringPaymentMethods,
} from "@rentiohq/shared-frontend/dist/utils/paymentRequest.utils";
import CONFIG from "@rentiohq/shared/dist/config/app.config";
import logger from "@rentiohq/shared/dist/logger";
import {
  EPaymentMethod,
  EPaymentRequestFeature,
  IPaymentRequest,
} from "@rentiohq/shared/dist/types/payment.types";
import { IProperty } from "@rentiohq/shared/dist/types/property.types";
import { default as React } from "react";
import { useNavigate, useParams } from "react-router";
import InfoHeader from "../../../../components/InfoHeader";
import Page from "../../../../components/Page";
import withAuthenticationCheck from "../../../../hoc/withAuthenticationCheck";
import withKycRequired from "../../../../hoc/withKycRequired";
import PaymentRequestRedirect from "../../components/PaymentRequestRedirect";
import * as infoUtils from "../../utils/info.utils";
import * as S from "./PaymentMethodSelect.styles";

const PaymentMethodSelectBase = () => {
  const navigate = useNavigate();
  const params = useParams();

  const [selectedPaymentMethod, setSelectedPaymentMethod] = React.useState<
    EUIPaymentMethod | undefined
  >();
  const [pendingPressSelect, setPendingPressSelect] = React.useState(false);

  // Data
  const paymentRequestId = params.paymentRequestId
    ? parseInt(params.paymentRequestId, 0)
    : undefined;

  const callbackUrl = paymentRequestId
    ? `${window.origin}/payment/${paymentRequestId}/status`
    : undefined;

  // Event handlers
  const handlePressMoreInfo = (paymentRequest: IPaymentRequest) => () => {
    const url = getLocalizedText(`payment.online.select_method.cta.info.url`);
    window.open(url, "_blank");
  };

  const handlePressRecurringInfo = () => {
    const url = getLocalizedText(
      "payment.online.select_method.cta.info_recurring.url",
    );
    window.open(url, "_blank");
  };

  const handlePressOnceInfo = () => {
    const url = getLocalizedText(
      "payment.online.select_method.cta.info_once.url",
    );
    window.open(url, "_blank");
  };

  const handlePressSelectPaymentMethod =
    (newSelectedPaymentMethod: EUIPaymentMethod) => () => {
      setSelectedPaymentMethod(newSelectedPaymentMethod);
    };

  const handlePressSelect = (paymentRequest: IPaymentRequest) => () => {
    if (!selectedPaymentMethod) {
      showAlert({
        type: "info",
        message: getLocalizedText(
          "payment.online.select_method.cta_info.select",
        ),
      });

      return;
    }

    if (!paymentRequestId) {
      return;
    }

    switch (selectedPaymentMethod) {
      case EUIPaymentMethod.RecurringDirectDebit:
        navigate(
          `/payment/${paymentRequestId}/payment-method/mangopay?type=recurring`,
        );
        break;

      case EUIPaymentMethod.OnceDirectDebit:
        navigate(
          `/payment/${paymentRequestId}/payment-method/mangopay?type=once`,
        );
        break;

      case EUIPaymentMethod.RecurringBankWire:
      case EUIPaymentMethod.OnceBankWire:
        navigate(`/payment/${paymentRequestId}/payment-method/bank-wire`);
        break;

      case EUIPaymentMethod.RecurringIbanisation:
      case EUIPaymentMethod.OnceIbanisation:
        navigate(`/payment/${paymentRequestId}/payment-method/ibanisation`);
        break;

      case EUIPaymentMethod.OnceBancontact:
        if (paymentRequest.amount > CONFIG.PAYMENT_LIMIT_AMOUNT_NOTIFICATION) {
          navigate(
            `/payment/${paymentRequestId}/payment-method/${EPaymentMethod.Bancontact}/payment-limit`,
          );
          break;
        }

        handlePressWebPayment({
          paymentMethod: EPaymentMethod.Bancontact,
          paymentRequest,
        });
        break;

      case EUIPaymentMethod.OnceMaestro:
        handlePressWebPayment({
          paymentMethod: EPaymentMethod.Maestro,
          paymentRequest,
        });
        break;

      case EUIPaymentMethod.OnceIdeal:
        if (paymentRequest.amount > CONFIG.PAYMENT_LIMIT_AMOUNT_NOTIFICATION) {
          navigate(
            `/payment/${paymentRequestId}/payment-method/${EPaymentMethod.Ideal}/payment-limit`,
          );
          break;
        }

        handlePressWebPayment({
          paymentMethod: EPaymentMethod.Ideal,
          paymentRequest,
        });
        break;

      default:
        break;
    }
  };

  const handlePressWebPayment = async (params: {
    paymentMethod: EPaymentMethod;
    paymentRequest: IPaymentRequest;
  }) => {
    if (!paymentRequestId || !callbackUrl) {
      return;
    }

    const { paymentMethod, paymentRequest } = params;

    setPendingPressSelect(true);

    try {
      // Legacy Mollie flow
      if (!paymentRequest.features.includes(EPaymentRequestFeature.BcmcV2)) {
        const { data } = await paymentApi.getMollieUrl(
          paymentRequestId,
          callbackUrl,
        );

        const { redirectUrl } = data;

        window.open(redirectUrl, "_self");
        return;
      }

      const { data } = await paymentApi.generateWebPayment(
        paymentRequestId,
        paymentMethod,
        callbackUrl,
      );

      const { redirectUrl } = data;

      if (!redirectUrl) {
        throw new Error("No redirectUrl or templateUrl found for web payment");
      }

      setTimeout(() => {
        window.open(redirectUrl, "_self");
      }, 20);
    } catch (unknownError) {
      const error = unknownError as any;
      logger.logError({ error });

      showAlert({ error, type: "error" });

      setPendingPressSelect(false);
    }
  };

  // Render
  const renderPaymentRequest = (paymentRequest: IPaymentRequest) => {
    return (
      <PropertyFetchListItem
        property={paymentRequest.property}
        propertyId={paymentRequest.requestPropertyId}
        renderAlways={(property?: IProperty) => {
          return renderPaymentRequestAndProperty(paymentRequest, property);
        }}
      />
    );
  };

  const renderPaymentRequestAndProperty = (
    paymentRequest: IPaymentRequest,
    property?: IProperty,
  ) => {
    const recurringPaymentMethods =
      getAvailableRecurringPaymentMethods(paymentRequest);
    const oncePaymentMethods = getAvailableOncePaymentMethods(paymentRequest);

    const hasOnceAndRecurringPaymentMethods =
      recurringPaymentMethods.length > 0 && oncePaymentMethods.length > 0;

    return (
      <Page>
        <InfoHeader
          title={infoUtils.getTitle({
            postfix: "payment_method_select",
            paymentRequest,
            property,
            withAllowedPaymentMethodsVariation: true,
          })}
          body={infoUtils.getBody({
            postfix: "payment_method_select",
            paymentRequest,
            property,
            withAllowedPaymentMethodsVariation: true,
          })}
          buttonTitle={getLocalizedText(
            "payment.online.select_method.cta.info.title",
          )}
          onPressButton={handlePressMoreInfo(paymentRequest)}
        />
        <S.LisWrap>
          {recurringPaymentMethods.length > 0 && (
            <>
              {hasOnceAndRecurringPaymentMethods && (
                <S.ListItemSelectWrap>
                  <FormSectionSubtitle
                    title={getLocalizedText(
                      "payment.online.select_method.header.recurring",
                    )}
                    onPressInfoIcon={handlePressRecurringInfo}
                  />
                </S.ListItemSelectWrap>
              )}
              {renderPaymentMethods(recurringPaymentMethods)}

              <Spacer weight={ESpacerWeight.W24} />
            </>
          )}

          {oncePaymentMethods.length > 0 && (
            <>
              {hasOnceAndRecurringPaymentMethods && (
                <S.ListItemSelectWrap>
                  <FormSectionSubtitle
                    title={getLocalizedText(
                      "payment.online.select_method.header.once",
                    )}
                    onPressInfoIcon={handlePressOnceInfo}
                  />
                </S.ListItemSelectWrap>
              )}
              {renderPaymentMethods(oncePaymentMethods)}

              <Spacer weight={ESpacerWeight.W24} />
            </>
          )}

          <S.ButtonDisabledInfo>
            {getLocalizedText("payment.online.select_method.cta_info.select")}
          </S.ButtonDisabledInfo>

          <S.ButtonPrimaryWrap>
            <ButtonPrimary
              title={getLocalizedText(
                "payment.online.select_method.cta.select",
              )}
              isLoading={pendingPressSelect}
              onPress={handlePressSelect(paymentRequest)}
              buttonSize={EButtonSize.Full}
            />
          </S.ButtonPrimaryWrap>
        </S.LisWrap>
      </Page>
    );
  };

  const renderPaymentMethods = (paymentMethods: EUIPaymentMethod[]) => (
    <>
      {paymentMethods.map(paymentMethod => (
        <React.Fragment key={paymentMethod}>
          <S.ListItemSelectWrap
            selected={selectedPaymentMethod === paymentMethod}
          >
            <ListItemSelect
              title={getLocalizedText(
                `payment.online.select_method.cta.${paymentMethod}`,
              )}
              selected={selectedPaymentMethod === paymentMethod}
              onPress={handlePressSelectPaymentMethod(paymentMethod)}
              extraSubtitle={renderPaymentMethodIcon(paymentMethod)}
              showSelectionLeft={true}
            />
          </S.ListItemSelectWrap>
          <ListItemSeparator />
        </React.Fragment>
      ))}
    </>
  );

  const renderPaymentMethodIcon = (paymentMethod: EUIPaymentMethod) => {
    switch (paymentMethod) {
      case EUIPaymentMethod.RecurringDirectDebit:
      case EUIPaymentMethod.OnceDirectDebit:
        return <S.PaymentMethodIconSepa />;

      case EUIPaymentMethod.OnceBancontact:
        return <S.PaymentMethodIconBcmc />;

      case EUIPaymentMethod.OnceMaestro:
        return <S.PaymentMethodIconMaestro />;

      case EUIPaymentMethod.OnceIdeal:
        return <S.PaymentMethodIconIdeal />;

      case EUIPaymentMethod.RecurringIbanisation:
        return getLocalizedText(
          "payment.online.select_method.cta.recurring_ibanisation.subtitle",
        );

      case EUIPaymentMethod.OnceIbanisation:
        return getLocalizedText(
          "payment.online.select_method.cta.once_ibanisation.subtitle",
        );

      default:
        break;
    }
  };

  const renderLoading = () => (
    <Page withScroll={false}>
      <FullScreenActivityIndicator />
    </Page>
  );

  const renderError = (retry: () => void, error?: Error) => (
    <Page withScroll={false}>
      <FullScreenErrorState
        showIcon={false}
        error={error}
        title={getLocalizedText("general.error")}
        actionTitle={getLocalizedText("try.again")}
        onPressAction={retry}
      />
    </Page>
  );

  if (!paymentRequestId) {
    return null;
  }

  return (
    <>
      <PaymentRequestRedirect id={paymentRequestId} />
      <PaymentRequestFetch
        id={paymentRequestId}
        renderPaymentRequest={renderPaymentRequest}
        renderLoading={renderLoading}
        renderError={renderError}
      />
    </>
  );
};

export const PaymentMethodSelect = withAuthenticationCheck(
  withKycRequired(PaymentMethodSelectBase),
);
